import {
  ButtonERP,
  ButtonFilterERP,
  ButtonPrimeReact,
  Checkbox,
  SearchInput,
} from "@finanedi.es/storybook"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { AsientosContablesQueryParamsProps } from "api/AsientoContable.service"
import { ESTADO_ACTIVO } from "api/Types/EstadoActivo"
import { SortingCriteria } from "api/interfaces/SortingCriteria"
import { AnimatePresence } from "framer-motion"
import { Calendar } from "primereact/calendar"
import { Dialog } from "primereact/dialog"
import { Menu } from "primereact/menu"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useHistory } from "react-router-dom"
import { IMPORTAR_ACTIVOS } from "routes/RoutePaths"
import { backendDateFormat } from "utils/formatDate"
import { EstadoActivos } from "./components/EstadoActivos"

export const FilterMenu = ({
  serviceQuery,
  sortingCriteria,
  skipCount,
  maxResultCount,
  createActivo,
  exportToExcel,
  resetPagination,
}: {
  serviceQuery: $TSMigrationFixMe
  sortingCriteria?: SortingCriteria
  skipCount?: AsientosContablesQueryParamsProps["skipCount"]
  maxResultCount?: AsientosContablesQueryParamsProps["maxResultCount"]
  createActivo?: () => void
  exportToExcel?: (object: $TSMigrationFixMe) => void
  resetPagination?: () => void
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const history = useHistory()
  const menu = useRef<Menu>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const [date, setDate] = useState<Record<string, string | undefined>>()
  const [keyword, setKeyword] = useState<string | undefined>()
  const [estado, setEstado] = useState({
    [ESTADO_ACTIVO.PENDIENTE]: false,
    [ESTADO_ACTIVO.ACTIVO]: false,
    [ESTADO_ACTIVO.DADO_DE_BAJA]: false,
    [ESTADO_ACTIVO.AMORTIZADO]: false,
  })
  const handleEstado = (checkbox: keyof typeof estado) => {
    setEstado((prevEstado) => ({
      ...prevEstado,
      [checkbox]: !prevEstado[checkbox],
    }))
  }

  const openFilters = () => {
    setMenuOpen(true)
  }
  const queryFixer = (queryParams: $TSMigrationFixMe) => {
    const asesoriaParams = {
      Estados: queryParams.Estados,
      FechaLowerLimit: queryParams.FechaLowerLimit,
      FechaUpperLimit: queryParams.FechaUpperLimit,
      Keyword: queryParams.Keyword,
      MaxResultCount: queryParams.MaxResultCount,
      SkipCount: queryParams.SkipCount,
      SortingCriteria:
        queryParams.SortingCriteria?.property &&
        queryParams.SortingCriteria?.sort
          ? `${queryParams.SortingCriteria.property} ${queryParams.SortingCriteria.sort}`
          : "FechaAdquisicion DESC, Identificador DESC",
    }
    const fixedQueryParams = Object.fromEntries(
      Object.entries(asesoriaParams).filter(
        ([_key, value]) => value || value === 0 // eslint-disable-line @typescript-eslint/no-unused-vars
      )
    )
    return fixedQueryParams
  }
  const query = {
    Estados: Object.entries(estado)
      .filter(([_key, value]) => value) // eslint-disable-line @typescript-eslint/no-unused-vars
      .flatMap(([key]) =>
        key === `${ESTADO_ACTIVO.PENDIENTE}`
          ? ESTADO_ACTIVO.PENDIENTE
          : key === `${ESTADO_ACTIVO.ACTIVO}`
          ? ESTADO_ACTIVO.ACTIVO
          : key === `${ESTADO_ACTIVO.DADO_DE_BAJA}`
          ? ESTADO_ACTIVO.DADO_DE_BAJA
          : key === `${ESTADO_ACTIVO.AMORTIZADO}`
          ? ESTADO_ACTIVO.AMORTIZADO
          : []
      ),
    FechaLowerLimit: date?.min && backendDateFormat(new Date(date.min)),
    FechaUpperLimit: date?.max && backendDateFormat(new Date(date.max)),
    SortingCriteria: sortingCriteria,
    SkipCount: skipCount,
    MaxResultCount: maxResultCount,
    Keyword: keyword,
  }
  const handleQuery = () => {
    serviceQuery(queryFixer(query))
  }
  const handleApplyFilters = () => {
    setMenuOpen(false)
  }
  const hideFilter = () => {
    setMenuOpen(!menuOpen)
  }
  const handleResetFilters = () => {
    setEstado({
      [ESTADO_ACTIVO.PENDIENTE]: false,
      [ESTADO_ACTIVO.ACTIVO]: false,
      [ESTADO_ACTIVO.DADO_DE_BAJA]: false,
      [ESTADO_ACTIVO.AMORTIZADO]: false,
    })
    setDate({ min: undefined, max: undefined })
  }

  const appliedFiltersTags = [
    {
      label: t("nsAssets.filters.status.tags", {
        status: t("nsAssets.filters.status.pending"),
      }),
      value: estado[ESTADO_ACTIVO.PENDIENTE] ?? false,
      action: () => handleEstado(ESTADO_ACTIVO.PENDIENTE),
    },
    {
      label: t("nsAssets.filters.status.tags", {
        status: t("nsAssets.filters.status.active"),
      }),
      value: estado[ESTADO_ACTIVO.ACTIVO] ?? false,
      action: () => handleEstado(ESTADO_ACTIVO.ACTIVO),
    },
    {
      label: t("nsAssets.filters.status.tags", {
        status: t("nsAssets.filters.status.withdrawn"),
      }),
      value: estado[ESTADO_ACTIVO.DADO_DE_BAJA] ?? false,
      action: () => handleEstado(ESTADO_ACTIVO.DADO_DE_BAJA),
    },
    {
      label: t("nsAssets.filters.status.tags", {
        status: t("nsAssets.filters.status.amortized"),
      }),
      value: estado[ESTADO_ACTIVO.AMORTIZADO] ?? false,
      action: () => handleEstado(ESTADO_ACTIVO.AMORTIZADO),
    },
    {
      label: `${t("nsAssets.filters.date.since")}:`,
      value: date?.min
        ? Intl.DateTimeFormat("es-ES", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          }).format(new Date(date.min))
        : undefined,
      action: () => setDate({ ...date, min: undefined }),
    },
    {
      label: `${t("nsAssets.filters.date.until")}:`,
      value: date?.max
        ? Intl.DateTimeFormat("es-ES", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          }).format(new Date(date.max))
        : undefined,
      action: () => setDate({ ...date, max: undefined }),
    },
  ]
  const appliedFilters = appliedFiltersTags.filter((filter) => filter.value)

  useEffect(() => {
    resetPagination?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword, appliedFilters?.length])

  useEffect(() => {
    handleQuery()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    keyword,
    skipCount,
    maxResultCount,
    appliedFilters?.length,
    date?.min,
    date?.max,
    sortingCriteria?.sort,
    sortingCriteria?.property,
  ])

  return (
    <>
      <div className='!tw-px-6 tw-flex tw-flex-col lg:tw-flex-row tw-justify-between tw-items-center tw-pb-4 tw-pt-6 tw-shadow-3xl tw-rounded-t-xl mb-1 tw-bg-white'>
        <p className='tw-whitespace-nowrap tw-text-xl tw-text-text tw-my-auto'>
          {t("nsAssets.mainHeader.title")}
        </p>
        <div className='tw-flex tw-w-full tw-justify-center lg:tw-justify-end'>
          <div className='!tw-w-fit tw-flex tw-items-center !tw-flex-row tw-justify-end tw-flex-wrap'>
            <div className='tw-grid tw-gap-3 lg:tw-flex lg:tw-flex-row md:tw-grid-cols-1 tw-items-center tw-w-fit'>
              <SearchInput
                placeholder={`${t("nsAssets.mainHeader.searchPlaceholder")}`}
                onChange={(ev: string) => {
                  resetPagination?.()
                  setKeyword(ev)
                }}
                inputValue={keyword}
                className='tw-mb-1 sm:tw-mb-0 tw-h-10 tw-w-full lg:tw-w-72'
              />
              <ButtonFilterERP
                isActive={appliedFilters.length > 0}
                numberActive={appliedFilters?.length}
                onClick={openFilters}
                deleteFilter={handleResetFilters}
                filterButtonLabel={`${t("filtros.title")}`}
              />
              <div className='tw-grid tw-grid-cols-3 lg:tw-flex tw-gap-3'>
                <ButtonERP
                  customize={{
                    size: "md",
                    type: "secondary",
                  }}
                  onClick={createActivo}
                  label={`${t("nsAssets.mainHeader.addAsset")}`}
                />
                <div className='tw-col-start-3 tw-grid tw-place-content-center tw-border-solid tw-border tw-rounded-md xl:tw-max-w-10 hover:!tw-bg-gray-100'>
                  <Menu
                    popup
                    ref={menu}
                    id='popup_menu'
                    className='!tw-py-1'
                    model={[
                      {
                        label: `${t("mayor.importButton")}`,
                        icon: "pi pi-file",
                        command: () => history.push(IMPORTAR_ACTIVOS),
                      },
                      {
                        label: `${t("mayor.exportButton")}`,
                        icon: "pi pi-file-excel",
                        command: exportToExcel,
                      },
                    ]}
                  />
                  <div
                    className='tw-grid tw-place-content-center tw-cursor-pointer tw-size-10 tw-rounded-md'
                    onClick={(event) => menu.current?.toggle(event)}
                    aria-controls='popup_menu'
                    aria-haspopup
                  >
                    <FontAwesomeIcon
                      icon={["fal", "ellipsis-v"]}
                      aria-controls='popup_menu'
                      aria-haspopup
                      className='tw-text-black tw-size-5'
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        <Dialog
          breakpoints={{ "575px": "100vw" }}
          className='!tw-m-0 !tw-h-full tw-select-none'
          header={t("filtros.title")}
          visible={menuOpen}
          footer={
            <div className='tw-w-full tw-border-t-2 tw-pt-6'>
              <ButtonPrimeReact
                className='tw-ml-2 !tw-bg-primary hover:!tw-bg-primary-over tw-cursor-pointer !tw-w-full'
                onClick={handleApplyFilters}
              >
                {t("filtros.labelApply")}
              </ButtonPrimeReact>
            </div>
          }
          onHide={() => {
            hideFilter()
          }}
          draggable={false}
          resizable={false}
          position='right'
          closable
          closeOnEscape
        >
          {!!appliedFilters?.length && (
            <div className='tw-mb-8'>
              <div className='tw-flex tw-mb-4 tw-justify-between'>
                <span className='tw-font-medium'>
                  {t("tickets.filter.appliedFilters")}
                </span>
                <span
                  onClick={handleResetFilters}
                  className='tw-text-text-hyperlinks tw-cursor-pointer'
                >
                  {t("tickets.filter.deleteAll")}
                </span>
              </div>
              <div className='tw-flex tw-flex-wrap tw-mb-8 tw-gap-2 tw-font-normal tw-text-xs'>
                {appliedFilters.map((filter) => (
                  <div
                    key={filter.label}
                    onClick={filter.action}
                    className='tw-items-center tw-justify-between tw-cursor-pointer tw-bg-info-10 tw-text-info tw-rounded-md tw-border tw-border-info tw-py-1 tw-px-2'
                  >
                    <span>
                      {filter.label} {filter.value}
                    </span>
                    <FontAwesomeIcon
                      icon={["fal", "xmark"]}
                      className='tw-ml-2 tw-text-info'
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className='tw-mb-8'>
            <div className='tw-mb-4'>
              <span className='tw-font-medium'>
                {t("nsAssets.filters.status.title")}
              </span>
            </div>
            <div className='tw-flex tw-items-center tw-py-1'>
              <Checkbox
                id='empresasGestionadas-filterMenu-validada'
                className='tw-mr-4'
                isChecked={estado[ESTADO_ACTIVO.PENDIENTE]}
                onClick={() => handleEstado(ESTADO_ACTIVO.PENDIENTE)}
              />
              <span
                className='tw-items-center tw-mb-0 tw-py-1 tw-cursor-pointer'
                onClick={() => handleEstado(ESTADO_ACTIVO.PENDIENTE)}
              >
                <EstadoActivos
                  variant={ESTADO_ACTIVO.PENDIENTE}
                  t={t}
                />
              </span>
            </div>
            <div className='tw-flex tw-items-center tw-py-1'>
              <Checkbox
                id='empresasGestionadas-filterMenu-sinValidar'
                className='tw-mr-4'
                isChecked={estado[ESTADO_ACTIVO.ACTIVO]}
                onClick={() => handleEstado(ESTADO_ACTIVO.ACTIVO)}
              />
              <span
                className='tw-items-center tw-mb-0 tw-py-1 tw-cursor-pointer'
                onClick={() => handleEstado(ESTADO_ACTIVO.ACTIVO)}
              >
                <EstadoActivos
                  variant={ESTADO_ACTIVO.ACTIVO}
                  t={t}
                />
              </span>
            </div>
            <div className='tw-flex tw-items-center tw-py-1'>
              <Checkbox
                id='empresasGestionadas-filterMenu-rechazadas'
                className='tw-mr-4'
                isChecked={estado[ESTADO_ACTIVO.DADO_DE_BAJA]}
                onClick={() => handleEstado(ESTADO_ACTIVO.DADO_DE_BAJA)}
              />
              <span
                className='tw-items-center tw-mb-0 tw-py-1 tw-cursor-pointer'
                onClick={() => handleEstado(ESTADO_ACTIVO.DADO_DE_BAJA)}
              >
                <EstadoActivos
                  variant={ESTADO_ACTIVO.DADO_DE_BAJA}
                  t={t}
                />
              </span>
            </div>
            <div className='tw-flex tw-items-center tw-py-1'>
              <Checkbox
                id='empresasGestionadas-filterMenu-rechazadas'
                className='tw-mr-4'
                isChecked={estado[ESTADO_ACTIVO.AMORTIZADO]}
                onClick={() => handleEstado(ESTADO_ACTIVO.AMORTIZADO)}
              />
              <span
                className='tw-items-center tw-mb-0 tw-py-1 tw-cursor-pointer'
                onClick={() => handleEstado(ESTADO_ACTIVO.AMORTIZADO)}
              >
                <EstadoActivos
                  variant={ESTADO_ACTIVO.AMORTIZADO}
                  t={t}
                />
              </span>
            </div>
          </div>

          <div className='tw-mb-8'>
            <div className='tw-mb-4'>
              <span className='tw-font-medium'>
                {t("nsAssets.filters.date.title")}
              </span>
            </div>
            <div className='tw-flex tw-flex-col mobile:tw-flex-row tw-mb-6'>
              <div className='tw-flex tw-flex-col tw-flex-grow tw-mb-4 mobile:tw-mb-0'>
                <label>{t("nsAssets.filters.date.since")}</label>
                <Calendar
                  id='fechaDesde'
                  name='fechaDesde'
                  clearButtonClassName='!tw-hidden'
                  value={date?.min ? new Date(date.min) : undefined}
                  locale={language}
                  onChange={(e) => {
                    const eventDate = e.target.value as unknown as string
                    setDate({
                      max:
                        date?.max && new Date(eventDate) > new Date(date?.max)
                          ? undefined
                          : date?.max,
                      min: eventDate,
                    })
                  }}
                  placeholder='dd/mm/aaaa'
                  dateFormat='dd/mm/yy'
                  mask='99/99/9999'
                  className='w-full'
                  showIcon
                  panelClassName='!tw-z-[2302]'
                />
              </div>
              <div className='tw-hidden mobile:tw-block tw-py-3 tw-px-3 tw-items-baseline tw-mt-auto tw-mb-0'>
                -
              </div>
              <div className='tw-flex tw-flex-col tw-flex-grow'>
                <label>{t("nsAssets.filters.date.until")}</label>
                <Calendar
                  id='fechaHasta'
                  name='fechaHasta'
                  clearButtonClassName='!tw-hidden'
                  value={date?.max ? new Date(date.max) : undefined}
                  locale={language}
                  onChange={(e) => {
                    const eventDate = e.target.value as unknown as string
                    setDate({
                      min:
                        date?.min && new Date(eventDate) < new Date(date?.min)
                          ? undefined
                          : date?.min,
                      max: eventDate,
                    })
                  }}
                  placeholder='dd/mm/aaaa'
                  dateFormat='dd/mm/yy'
                  mask='99/99/9999'
                  className='w-full'
                  showIcon
                  panelClassName='!tw-z-[2302]'
                />
              </div>
            </div>
          </div>
        </Dialog>
      </AnimatePresence>
    </>
  )
}
